import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import "./HomePage.css"; // reuse the animated text style

export default function AccountDeletionPage() {
  return (
    <Box marginTop={4}>
      <Stack
        justifyContent="center"
        alignItems="center"
        gap={{ xs: 3, md: 5 }}
        paddingX={0}
      >
        {/* Animated main heading */}
        <Typography
          className="animate-character"
          variant={"h3"}
          fontWeight="bold"
          align="center"
          sx={{
            "@media (max-width: 600px)": {
              fontSize: "2.2rem",
              letterSpacing: 0.5,
            },
            "@media (min-width: 601px) and (max-width: 900px)": {
              fontSize: "3.2rem",
              letterSpacing: 0.5,
            },
            "@media (min-width: 901px)": {
              fontSize: "4rem",
              letterSpacing: 0.5,
            },
          }}
        >
          Account Deletion
        </Typography>

        {/* Decorative dividers */}
        <Box sx={{ width: "100%" }}>
          <Divider />
          <Divider />
        </Box>

        {/* Content container */}
        <Box
          sx={{
            width: { xs: "95%", md: "80%" },
            maxWidth: "900px",
            textAlign: "left",
          }}
        >
          {/* Introduction */}
          <Divider sx={{ width: "100%", mb: 2, mt: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              Requesting Account Deletion
            </Typography>
          </Divider>
          <Typography paragraph>
            You have the right to delete your SainiAudiobooks account at any
            time. Once deleted, your account and associated data will be
            permanently removed from our systems, subject to any legal
            obligations that may require us to retain certain information.
          </Typography>

          {/* How to Delete */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              How to Request Deletion
            </Typography>
          </Divider>
          <Typography paragraph>
            To initiate the account deletion process, please send an email to:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              my: 2,
              p: 2,
              bgcolor: "rgba(0,0,0,0.04)",
              borderRadius: 2,
              fontFamily: "monospace",
            }}
          >
            <strong>info@sainiaudiobooks.com</strong>
          </Typography>
          <Typography paragraph>
            In your email, please include:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>
              The email address and/or phone number associated with your
              account.
            </li>
            <li>
              Your full name as registered (to help us verify your identity).
            </li>
            <li>
              A clear statement that you wish to permanently delete your
              account.
            </li>
          </Typography>
          <Typography paragraph>
            For security reasons, we may request additional verification before
            processing your request. Please allow up to <strong>7 business
            days</strong> for the deletion to be completed.
          </Typography>

          {/* What Happens */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              What Happens After Deletion
            </Typography>
          </Divider>
          <Typography paragraph>
            Once your account is deleted:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>
              You will no longer be able to log in or access any content
              purchased or saved in your account.
            </li>
            <li>
              All personal data associated with your account will be
              permanently erased from our active databases.
            </li>
            <li>
              Any active subscriptions will be cancelled and will not be
              eligible for refunds (unless a technical issue occurred, as
              outlined in our refund policy).
            </li>
            <li>
              Your profile information, including any uploaded profile images,
              will be removed.
            </li>
          </Typography>
          <Typography paragraph>
            Please note that we may retain certain information if required by
            law (e.g., transaction records for tax purposes) or to resolve
            disputes. Such data will be anonymised where possible and kept only
            for as long as legally necessary.
          </Typography>

          {/* Important Considerations */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Important Considerations
            </Typography>
          </Divider>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>
              <strong>No refunds:</strong> Account deletion is final and
              irreversible. We do not provide refunds for any remaining
              subscription time.
            </li>
            <li>
              <strong>Data backup:</strong> Please ensure you have saved any
              information or content you wish to keep before requesting
              deletion.
            </li>
            <li>
              <strong>Third‑party services:</strong> Deleting your account does
              not automatically cancel any third‑party services you may have
              linked (e.g., Razorpay payment agreements). You must manage those
              separately.
            </li>
          </Typography>

          {/* Contact for Assistance */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Need Help?
            </Typography>
          </Divider>
          <Typography paragraph>
            If you have any questions about the account deletion process or
            need assistance, please reach out to our support team:
          </Typography>
          <Typography paragraph>
            <strong>Email:</strong> info@sainiaudiobooks.com
            <br />
            We aim to respond within 2 business days.
          </Typography>

          {/* Last updated */}
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 4, textAlign: "center", color: "text.secondary" }}
          >
            Last Updated: 28 March 2026
          </Typography>
        </Box>

        {/* Back to Home button */}
        <Button
          size="large"
          variant="contained"
          color="primary"
          sx={{
            px: 4,
            py: 1.75,
            fontSize: "1.15rem",
            fontWeight: 700,
            letterSpacing: 0.5,
            borderRadius: 9999,
            background: "linear-gradient(90deg, #0072ff 0%, #00c6ff 100%)",
            boxShadow:
              "0 6px 18px rgba(0,114,255,0.3), 0 2px 6px rgba(0,0,0,0.1)",
            textTransform: "none",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow:
                "0 10px 24px rgba(0,114,255,0.4), 0 4px 10px rgba(0,0,0,0.12)",
              background:
                "linear-gradient(90deg, #0062e6 0%, #00b6ff 100%) !important",
            },
            "&:focus-visible": {
              outline: "3px solid #111",
              outlineOffset: "3px",
            },
          }}
          component={Link}
          to="/"
          aria-label="Return to homepage"
        >
          ← Return to Home
        </Button>

        {/* Bottom decorative dividers */}
        <Box sx={{ width: "100%", mt: 4 }}>
          <Divider />
          <Divider />
        </Box>
      </Stack>
    </Box>
  );
}